/** Typed fetch wrapper for the Static Web Apps managed API at /api. */

export type Role =
  | "ADMIN"
  | "STORE_MANAGER"
  | "MARKETING"
  | "BILLING_EXECUTIVE"
  | "PAYMENT_COLLECTION_AGENT"
  | "DELIVERY_AGENT"
  | "SERVICE_MANAGER"
  | "INDOOR_SERVICE_TECHNICIAN"
  | "OUTDOOR_SERVICE_TECHNICIAN";

export type Module =
  | "ADMIN"
  | "MARKETING"
  | "COLLECTIONS"
  | "SALES"
  | "SERVICE"
  | "DELIVERY"
  | "INVENTORY";

export interface SessionUser {
  id: string;
  name: string;
  /** Null for the password-only accounts (Admin, Store Manager). */
  mobileNumber: string | null;
  /** Null for the OTP-only accounts - every role except those two. */
  username: string | null;
  /**
   * The PRIMARY role, for a one-line "signed in as". Never decides what is
   * shown - `modules` from /auth/me does, and the server computes it from the
   * UNION of `roles`.
   */
  role: Role;
  /** Every role this person holds. One person can hold several. */
  roles: Role[];
  mustChangePassword: boolean;
}

export class ApiClientError extends Error {
  constructor(
    readonly status: number,
    override readonly message: string,
    readonly code: string,
    readonly details?: unknown,
  ) {
    super(message);
  }
}

const TOKEN_KEY = "supplyx.token";

export const tokenStore = {
  get(): string | null {
    try {
      return localStorage.getItem(TOKEN_KEY);
    } catch {
      // Private browsing or blocked storage - the session simply will not
      // survive a reload, which is better than crashing on load.
      return null;
    }
  },
  set(token: string) {
    try {
      localStorage.setItem(TOKEN_KEY, token);
    } catch {
      /* ignore */
    }
  },
  clear() {
    try {
      localStorage.removeItem(TOKEN_KEY);
    } catch {
      /* ignore */
    }
  },
};

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
): Promise<T> {
  const token = tokenStore.get();
  let response: Response;

  try {
    response = await fetch(`/api${path}`, {
      method,
      headers: {
        ...(body === undefined ? {} : { "Content-Type": "application/json" }),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    });
  } catch {
    // Distinguish "no network" from "server said no" - field staff need to
    // know which of those they are looking at.
    throw new ApiClientError(
      0,
      "No connection. Check your signal and try again.",
      "NETWORK_ERROR",
    );
  }

  if (response.status === 204) return undefined as T;

  let payload: unknown;
  try {
    payload = await response.json();
  } catch {
    payload = undefined;
  }

  if (!response.ok) {
    const error = (payload as { error?: { message?: string; code?: string; details?: unknown } })
      ?.error;
    throw new ApiClientError(
      response.status,
      error?.message ?? "Something went wrong.",
      error?.code ?? "UNKNOWN",
      error?.details,
    );
  }

  return payload as T;
}

/**
 * The lists that can be exported, and the name each is known by on the server.
 *
 * A union rather than a string, so a screen naming a dataset that does not
 * exist fails to compile instead of failing at the click - the old CSV export
 * needed no such name, so there is no habit of getting it right.
 *
 * The server's registry (src/services/export.service.ts) holds the matching
 * keys, and a test asserts the two sets are identical: a dataset added on one
 * side only is a menu item that 404s, or an export nothing offers.
 */
export type ExportDataset =
  | "orders"
  | "pre-orders"
  | "bills"
  | "outstanding"
  | "payments"
  | "service-jobs"
  | "deliveries"
  | "stock-levels"
  | "movements"
  | "products"
  | "users";

export type ExportFormat = "xlsx" | "pdf";

/**
 * Downloads an export and hands the browser the file.
 *
 * Fetched rather than linked. The session is a bearer token in
 * localStorage, so `<a href="/api/exports/orders">` arrives with no
 * Authorization header and is refused - the file has to come back through
 * the same authenticated path as everything else, and then be handed to the
 * browser as a blob.
 *
 * The filename comes from the response's Content-Disposition, so the server
 * names the file. Two places deciding what a download is called is two
 * places to change when it needs a date in it.
 */
export async function downloadExport(
  dataset: ExportDataset,
  format: ExportFormat,
): Promise<void> {
  const token = tokenStore.get();
  let response: Response;
  try {
    response = await fetch(`/api/exports/${dataset}?format=${format}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
  } catch {
    throw new ApiClientError(
      0,
      "No connection. Check your signal and try again.",
      "NETWORK_ERROR",
    );
  }

  if (!response.ok) {
    // An error comes back as JSON even though the success case is a file, so
    // the real message reaches the user rather than "could not download".
    let message = "The export could not be built.";
    let code = "UNKNOWN";
    try {
      const payload = (await response.json()) as {
        error?: { message?: string; code?: string };
      };
      message = payload.error?.message ?? message;
      code = payload.error?.code ?? code;
    } catch {
      /* Not JSON. Keep the generic message. */
    }
    throw new ApiClientError(response.status, message, code);
  }

  const disposition = response.headers.get("Content-Disposition") ?? "";
  const named = /filename="([^"]+)"/.exec(disposition);
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = named?.[1] ?? `${dataset}.${format}`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Revoked on the next tick, not immediately: Safari has not always
  // finished reading the blob by the time click() returns.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

export const api = {
  get: <T>(path: string) => request<T>("GET", path),
  post: <T>(path: string, body?: unknown) => request<T>("POST", path, body),
  patch: <T>(path: string, body?: unknown) => request<T>("PATCH", path, body),

  requestOtp: (mobileNumber: string) =>
    request<{
      maskedMobileNumber: string;
      expiresInSeconds: number;
      devCode?: string;
    }>("POST", "/auth/request-otp", { mobileNumber }),

  verifyOtp: (mobileNumber: string, code: string) =>
    request<{ token: string; expiresAt: string; user: SessionUser }>(
      "POST",
      "/auth/verify-otp",
      { mobileNumber, code },
    ),

  /**
   * "Your work now": counts only, one request, scoped by the server to the
   * modules and the rows this person may actually open. See
   * src/services/worklist.service.ts for why it is not four list calls.
   */
  myWork: () => request<MyWork>("GET", "/my-work"),

  me: () =>
    request<{
      user: SessionUser;
      modules: Module[];
      mustChangePassword: boolean;
      canUsePassword: boolean;
    }>("GET", "/auth/me"),

  /** Username + password, for Admin and Store Manager only. */
  login: (username: string, password: string) =>
    request<{
      token: string;
      expiresAt: string;
      mustChangePassword: boolean;
      user: SessionUser;
    }>("POST", "/auth/login", { username, password }),

  changePassword: (currentPassword: string, newPassword: string) =>
    request<void>("POST", "/auth/change-password", {
      currentPassword,
      newPassword,
    }),

  // --- customers (Q6: a managed list; field staff select, never create) ---

  customers: (query?: string, includeInactive = false) =>
    request<{ customers: Customer[] }>(
      "GET",
      `/customers?${new URLSearchParams({
        ...(query ? { q: query } : {}),
        ...(includeInactive ? { includeInactive: "true" } : {}),
        limit: "50",
      })}`,
    ),

  createCustomer: (input: { name: string; phone: string; address?: string }) =>
    request<{ customer: Customer }>("POST", "/customers", input),

  updateCustomer: (
    id: string,
    input: { name?: string; phone?: string; address?: string; isActive?: boolean },
  ) => request<{ customer: Customer }>("PATCH", `/customers/${id}`, input),

  bulkUploadCustomers: (csv: string) =>
    request<{ created: number; skippedExisting: number }>(
      "POST",
      "/customers/bulk-upload",
      { csv },
    ),

  // --- catalogue ---

  catalogue: (includeInactive = false) =>
    request<{ levels: string[]; categories: CategoryNode[] }>(
      "GET",
      `/catalogue${includeInactive ? "?includeInactive=true" : ""}`,
    ),

  /**
   * Adds a product to the master list. No price and no opening stock: a price
   * is an append to the price history and stock only moves through the
   * inventory ledger, so both are set afterwards, in the screens that own
   * them.
   */
  createProduct: (input: {
    sku: string;
    name: string;
    categoryId: string;
    aliceName?: string;
    hsnCode?: string;
    gstRate?: number;
    reorderLevel?: number;
  }) => request<{ product: Product }>("POST", "/products", input),

  /** The flat product list with each one's price in force today. */
  products: (query?: string, includeInactive = false) =>
    request<{ products: Product[] }>(
      "GET",
      `/products?${new URLSearchParams({
        ...(query ? { q: query } : {}),
        ...(includeInactive ? { includeInactive: "true" } : {}),
      })}`,
    ),

  priceHistory: (productId: string) =>
    request<{
      product: { id: string; sku: string; name: string };
      prices: PriceHistoryEntry[];
    }>("GET", `/products/${productId}/prices`),

  /**
   * Records a new price. Append-only - there is no edit or delete, because a
   * price already billed against must stay reproducible. A correction is a
   * new row with a later effective date.
   */
  recordPrice: (
    productId: string,
    input: {
      dealerPrice: number;
      mrp?: number | null;
      effectiveFrom: string;
      sourceNote?: string | null;
    },
  ) => request<{ price: PriceHistoryEntry }>("POST", `/products/${productId}/prices`, input),

  updateProduct: (
    id: string,
    // No unitPrice: a price is a dated row in the history, posted to
    // /products/:id/prices, not a column on the product.
    input: {
      reorderLevel?: number | null;
      name?: string;
      aliceName?: string | null;
      hsnCode?: string | null;
      gstRate?: number;
      categoryId?: string;
      isActive?: boolean;
    },
  ) => request<{ product: Product }>("PATCH", `/products/${id}`, input),

  // --- marketing ---

  createPreOrder: (input: CreatePreOrderInput) =>
    request<{ preOrder: { id: string; reference: string } }>(
      "POST",
      "/pre-orders",
      input,
    ),

  preOrders: (scope: "mine" | "queue" = "mine") =>
    request<{ preOrders: PreOrderSummary[] }>("GET", `/pre-orders?scope=${scope}`),

  lookupBill: (reference: string) =>
    request<{ bill: BillForCollection }>(
      "GET",
      `/bills/lookup?reference=${encodeURIComponent(reference)}`,
    ),

  /**
   * Change your own display name. Nothing else is accepted.
   *
   * A separate call from the Admin user editor on purpose: that one takes
   * roles, status, username and mobile number, and is gated on the ADMIN
   * module. This one has one field and no subject - the server renames
   * whoever is holding the session.
   */
  renameSelf: (name: string) =>
    // `/auth/me`, not `/me`. authRoutes mounts under /api/auth, so the
    // handler written as patch("/me") answers at /api/auth/me - and a
    // request to /api/me is a 404 that looks exactly like a missing feature.
    request<{ name: string; changed: boolean }>("PATCH", "/auth/me", { name }),

  recordPayment: (input: RecordPaymentInput) =>
    request<{
      billReference: string;
      amountCollected: string;
      outstandingAfter: string;
      fullySettled: boolean;
    }>("POST", "/payments", input),

  /**
   * A payment against a bill raised elsewhere.
   *
   * A different endpoint, not a flag on the one above. The two differ in
   * whether the amount can be capped at all, and an endpoint that decides
   * which rule applies from whether a field is present is an endpoint where
   * leaving a field out buys the weaker rule.
   *
   * No `outstandingAfter` and no `fullySettled` come back, because this
   * application never saw the bill and any figure it returned would be a
   * guess wearing the clothes of a fact.
   */
  recordExternalPayment: (input: RecordExternalPaymentInput) =>
    request<{
      paymentId: string;
      customerName: string;
      amountCollected: string;
      externalBillReference: string | null;
      voucherType: string;
      manualReceiptNo: string | null;
    }>("POST", "/payments/external", input),

  /**
   * The register: both kinds, newest first.
   *
   * Scoped by the SERVER - an agent gets their own receipts, the desk gets
   * everything - so there is no scope argument here to get wrong.
   */
  paymentsRecorded: () =>
    request<{ payments: RegisterEntry[] }>("GET", "/collections/payments"),

  // --- sales (Q2: the Billing Executive bills sales AND service orders) ---

  /** What is still owed on a day order, per line, with live stock beside it. */
  preOrderOutstanding: (preOrderId: string) =>
    request<{ preOrder: PreOrderRef; lines: OutstandingLine[] }>(
      "GET",
      `/pre-orders/${preOrderId}/outstanding`,
    ),

  /**
   * Confirms what can actually be supplied. Sends no prices: the server
   * resolves them from the Store Manager's price history (Q7).
   */
  createOrder: (input: {
    preOrderId: string;
    lineItems: { productId: string; quantityFulfilled: number }[];
    notes?: string;
  }) =>
    request<{ order: { id: string; reference: string; status: string } }>(
      "POST",
      "/orders",
      input,
    ),

  orders: (status?: string) =>
    request<{ orders: OrderSummary[] }>(
      "GET",
      `/orders${status ? `?status=${encodeURIComponent(status)}` : ""}`,
    ),

  order: (id: string) => request<{ order: OrderDetail }>("GET", `/orders/${id}`),

  /**
   * Generating the bill is the ONE action that moves stock (REQ-INV-1), and
   * the server refuses a second one for the same order - so a failed call
   * must never be retried blindly by the UI.
   */
  generateBill: (input: {
    orderId: string;
    discounts?: { lineItemId: string; discountAmount: number }[];
    paymentMethod?: string;
    /**
     * Who will deliver it. Omitted, a service order defaults to the agent who
     * collected the battery and a sales order is left for the Store Manager.
     */
    deliveryAgentId?: string;
  }) => request<{ bill: BillDetail }>("POST", "/bills", input),

  /**
   * Who a delivery can be given to. Role-gated rather than module-gated,
   * because the Billing Executive (SALES) and the Store Manager (DELIVERY)
   * both need it.
   */
  deliveryAgents: () =>
    request<{ agents: DeliveryAgent[] }>("GET", "/delivery-agents"),

  bills: () => request<{ bills: BillSummary[] }>("GET", "/bills"),

  bill: (id: string) => request<{ bill: BillDetail }>("GET", `/bills/${id}`),

  /**
   * Bills with money still outstanding, for the Collections module.
   *
   * Its own endpoint rather than /bills, which is gated on SALES: a
   * collection agent holds COLLECTIONS and nothing else.
   */
  outstandingBills: () =>
    request<{ bills: BillSummary[] }>("GET", "/collections/outstanding"),

  // --- service (section 8; each step narrows to the role that owns it) ---

  serviceJobs: (
    scope?: "queue" | "mine" | "delivery",
    status?: string,
    serial?: string,
  ) =>
    request<{ jobs: ServiceJob[] }>(
      "GET",
      `/service/jobs?${new URLSearchParams({
        ...(scope ? { scope } : {}),
        ...(status ? { status } : {}),
        ...(serial ? { serial } : {}),
      })}`,
    ),

  serviceJob: (id: string) =>
    request<{ job: ServiceJobDetail }>("GET", `/service/jobs/${id}`),

  /** Step 1: collection, with GPS and a photo (REQ-SVC-1). */
  collectServiceJob: (input: {
    customerId: string;
    /**
     * The catalogue MODEL, and the first thing entered after the customer.
     * Required, with no escape. The four category levels are derived from it
     * by the server and are deliberately not sent from here.
     */
    productId: string;
    /** Scanned or typed. Required for every new collection. */
    serialNumber: string;
    itemCount: number;
    capture: CapturePayloadDto;
  }) => request<{ job: ServiceJob }>("POST", "/service/jobs", input),

  /**
   * The models a battery can be collected as, with each one's place in the
   * tree. Fetched once and filtered on the device - see listCollectableModels
   * on the server for why this is not the product search, and why it carries
   * no prices.
   */
  serviceModels: () =>
    request<{ models: CollectableModel[]; levels: string[] }>(
      "GET",
      "/service/models",
    ),

  /** Step 2: the workshop confirms arrival. */
  receiveServiceJob: (id: string, notes?: string) =>
    request<{ job: ServiceJob }>(
      "POST",
      `/service/jobs/${id}/receive`,
      notes ? { notes } : {},
    ),

  /** Step 3: the diagnosis, which is one of four outcomes and nothing else. */
  diagnoseServiceJob: (
    id: string,
    input: {
      /** One of OK, FOC, PRORATA, REJECTED - and the whole decision now. */
      outcome: string;
      notes: string;
    },
  ) => request<{ job: ServiceJob }>("POST", `/service/jobs/${id}/diagnose`, input),

  /**
   * Step 4: carries the outcome out. Managers only.
   *
   * `lineItems` is for FOC and PRORATA - the two outcomes that issue a
   * battery, and so the two that raise an order, placed like any other. OK
   * and REJECTED return the customer's own battery, so they take no lines and
   * `order` comes back null.
   */
  completeServiceJob: (
    id: string,
    // No service charge: there is none for a repair, and a free replacement
    // is free by definition. The pro-rata amount below is a different thing -
    // what the customer pays towards a NEW battery, not a charge for the work.
    input: {
      lineItems?: { productId: string; quantity: number }[];
      /**
       * The flat pro-rata charge, before GST. Required for a PRORATA outcome
       * and refused for every other one, so an FOC cannot quietly carry a
       * charge and a pro-rata cannot arrive without its price.
       */
      prorataAmount?: number;
      notes?: string;
      /**
       * Who takes the battery back, the Service Manager's choice.
       *
       * Omit for "nobody named", which leaves the delivery in the Store
       * Manager's list. On an OK or a rejection it assigns the delivery task
       * outright; on a replacement it is recorded against the order and acted
       * on when the Billing Executive bills it.
       */
      deliveryAgentId?: string;
    },
  ) =>
    request<{
      order: { id: string; reference: string; type: string; status: string } | null;
      job: ServiceJob;
    }>("POST", `/service/jobs/${id}/complete`, input),

  // --- delivery (section 9; one queue for orders and service returns) ---

  /**
   * `unassigned` is the shared pool an agent pulls from, `mine` their own
   * round, `open` everything still to go out. Failed tasks appear in the first
   * two: a failed attempt is not the end of a delivery, the goods still have
   * to get there.
   */
  deliveryTasks: (scope: DeliveryScope = "open") =>
    request<{ tasks: DeliveryTask[] }>(
      "GET",
      `/delivery/tasks?${new URLSearchParams({ scope })}`,
    ),

  deliveryTask: (id: string) =>
    request<{ task: DeliveryTaskDetail }>("GET", `/delivery/tasks/${id}`),

  /**
   * Q8, both directions. No body is an agent claiming for themselves; naming
   * someone is a Store Manager pushing, which the API refuses to anyone else.
   */
  assignDelivery: (id: string, assignedToId?: string) =>
    request<{ task: DeliveryTask }>(
      "POST",
      `/delivery/tasks/${id}/assign`,
      assignedToId ? { assignedToId } : {},
    ),

  /** REQ-DLV-2: photo, GPS and signature, all three. */
  completeDelivery: (
    id: string,
    input: {
      capture: CapturePayloadDto;
      amountCollected?: number;
      /**
       * Who actually took the goods at the door. Optional, and staying that
       * way: deliveries go to whoever is behind the counter, and forcing a
       * value produces a column full of "customer".
       */
      receivedByName?: string;
    },
  ) => request<{ task: DeliveryTask }>("POST", `/delivery/tasks/${id}/deliver`, input),

  /**
   * A failed attempt. The capture is optional and takes no signature - there
   * was nobody at the door to give one.
   */
  failDelivery: (
    id: string,
    input: { reason: string; capture?: CapturePayloadDto },
  ) => request<{ task: DeliveryTask }>("POST", `/delivery/tasks/${id}/fail`, input),

  /**
   * Hand several stops to one agent at once. Managers only, all or nothing -
   * see assignTasksBulk in delivery.service for why a partial apply is worse
   * than a refusal.
   */
  assignDeliveriesBulk: (taskIds: string[], assignedToId: string) =>
    request<{ assigned: number; assignedTo: { id: string; name: string } }>(
      "POST",
      "/delivery/assign",
      { taskIds, assignedToId },
    ),

  /** The order a round runs in. The WHOLE round, in the order it should run. */
  reorderRound: (agentId: string, taskIds: string[]) =>
    request<{ updated: number }>("POST", "/delivery/round", { agentId, taskIds }),

  /**
   * Is the delivery queue in step with Sales and Service? Readable by anyone
   * with the module; closing the gaps is a manager's action.
   */
  deliveryGaps: () =>
    request<{ gaps: DeliveryQueueGaps; inStep: boolean }>(
      "GET",
      "/delivery/reconcile",
    ),

  /** Takes no body on purpose - it derives what is missing from the data. */
  reconcileDelivery: () =>
    request<{ ordersQueued: number; serviceJobsQueued: number; awaitingBilling: number }>(
      "POST",
      "/delivery/reconcile",
    ),

  // --- inventory (Store Manager and Admin; REQ-DATA-2 governs every write) ---

  stockLevels: (filters: { query?: string; onlyLow?: boolean } = {}) =>
    request<{ products: StockLevel[]; summary: StockSummary }>(
      "GET",
      `/inventory/levels?${new URLSearchParams({
        ...(filters.query ? { q: filters.query } : {}),
        ...(filters.onlyLow ? { onlyLow: "true" } : {}),
      })}`,
    ),

  /** Receiving goods. The only operation that raises stock. */
  recordStockIn: (input: {
    lineItems: { productId: string; quantity: number }[];
    reference?: string;
    notes?: string;
  }) =>
    request<{ movements: StockMovementResult[] }>("POST", "/inventory/stock-in", input),

  /**
   * A physical-count adjustment. Sends a DELTA, never a target quantity: a
   * target would silently discard any movement that happened while the count
   * was being typed.
   */
  correctStock: (input: {
    productId: string;
    quantityDelta: number;
    reason: string;
  }) =>
    request<{ movement: StockMovementResult; sku: string; name: string }>(
      "POST",
      "/inventory/corrections",
      input,
    ),

  stockMovements: (filters: { productId?: string; reason?: string } = {}) =>
    request<{ movements: StockMovement[] }>(
      "GET",
      `/inventory/movements?${new URLSearchParams({
        ...(filters.productId ? { productId: filters.productId } : {}),
        ...(filters.reason ? { reason: filters.reason } : {}),
        limit: "100",
      })}`,
    ),

  productMovements: (productId: string) =>
    request<{ movements: StockMovement[] }>(
      "GET",
      `/inventory/products/${productId}/movements`,
    ),

  /** Checks the cached quantity against the ledger. Reports drift, never repairs. */
  reconcileStock: () => request<Reconciliation>("GET", "/inventory/reconcile"),

  // --- admin (REQ-ADM-1; the ADMIN module, which only an Admin has) ---

  adminMeta: () =>
    request<{ roles: Role[]; statuses: string[]; auditActions: string[] }>(
      "GET",
      "/admin/meta",
    ),

  adminUsers: (filters: { role?: string; status?: string; query?: string } = {}) =>
    request<{ users: ManagedUser[] }>(
      "GET",
      `/admin/users?${new URLSearchParams({
        ...(filters.role ? { role: filters.role } : {}),
        ...(filters.status ? { status: filters.status } : {}),
        ...(filters.query ? { q: filters.query } : {}),
      })}`,
    ),

  adminUser: (id: string) =>
    request<{ user: ManagedUser }>("GET", `/admin/users/${id}`),

  /**
   * A password role gets `temporaryPassword` back exactly once. It is not
   * stored anywhere in clear, so a screen that receives one has to show it
   * until the operator has written it down.
   */
  createAdminUser: (input: {
    name: string;
    roles: string[];
    mobileNumber?: string;
    username?: string;
  }) =>
    request<{ user: ManagedUser; temporaryPassword?: string }>(
      "POST",
      "/admin/users",
      input,
    ),

  updateAdminUser: (
    id: string,
    input: {
      name?: string;
      /** The COMPLETE set the person should end up with, not a delta. */
      roles?: string[];
      status?: string;
      mobileNumber?: string | null;
      username?: string;
    },
  ) => request<{ user: ManagedUser }>("PATCH", `/admin/users/${id}`, input),

  issueUserPassword: (id: string, username?: string) =>
    request<{ user: ManagedUser; temporaryPassword: string }>(
      "POST",
      `/admin/users/${id}/password`,
      username ? { username } : {},
    ),

  unlockUser: (id: string) =>
    request<{ user: ManagedUser }>("POST", `/admin/users/${id}/unlock`, {}),

  auditEvents: (filters: { action?: string; entityType?: string } = {}) =>
    request<{ events: AuditEvent[] }>(
      "GET",
      `/admin/audit?${new URLSearchParams({
        ...(filters.action ? { action: filters.action } : {}),
        ...(filters.entityType ? { entityType: filters.entityType } : {}),
        limit: "100",
      })}`,
    ),

  /** Imported customers still missing a phone number, for the Store Manager. */
  customersMissingPhone: (offset = 0) =>
    request<{ customers: Customer[]; total: number }>(
      "GET",
      `/customers/missing-phone?limit=50&offset=${offset}`,
    ),
};

// --- shared shapes -------------------------------------------------------

export interface Customer {
  id: string;
  name: string;
  /** Null for a customer imported from the GST master - see the Store
   * Manager's "missing phone" list. */
  phone: string | null;
  address: string | null;
  isActive: boolean;
  gstType: string;
  gstNumber: string | null;
  state: string | null;
}

export interface Product {
  id: string;
  sku: string;
  name: string;
  aliceName?: string | null;
  hsnCode?: string | null;
  gstRate: number;
  /**
   * The dealer price in force today, as a fixed-2dp string, or NULL when the
   * product has never been priced. 10 of the 101 products in the master list
   * are unpriced on purpose, so the pickers must render "no price set" rather
   * than treating a missing price as free.
   */
  dealerPrice: string | null;
  mrp: string | null;
  priceEffectiveFrom: string | null;
  productPriceId: string | null;
  currentStockQty?: number;
  reorderLevel?: number | null;
  isActive: boolean;
  /**
   * Where this product sits, deepest first, each node carrying its parent.
   * GET /products has always returned this; nothing used to read it.
   *
   * It is what makes searching by model number possible: the hierarchy is
   * DERIVED from the product rather than chosen on the way to it.
   */
  category?: ProductCategoryChain;
}

export interface ProductCategoryChain {
  id: string;
  name: string;
  level: number;
  parent?: ProductCategoryChain | null;
}

/**
 * A model a battery can be COLLECTED as, which is a deliberately smaller
 * thing than a Product.
 *
 * No dealerPrice, no mrp, no stock: nothing on the collection screen is being
 * sold, and the technician holding the battery has no business with the
 * dealer price list. The server does not fetch those fields for this
 * endpoint, so they are absent rather than hidden - and their absence from
 * this type is what stops a screen quietly starting to show them.
 */
export interface CollectableModel {
  id: string;
  sku: string;
  name: string;
  /** Deepest-first, each node holding its parent. Pass it to hierarchyOf. */
  category: ProductCategoryChain;
}

/**
 * A node in the product hierarchy. One recursive shape for all four levels,
 * because the depth is data: COMPANY > MAIN_CATEGORY > SUB_CATEGORY >
 * SUB_CATEGORY today, and the master file already hints at a fifth grouping.
 * Products only ever hang off the deepest level.
 */
export interface CategoryNode {
  id: string;
  name: string;
  level: number;
  /** The identifier, e.g. "SUB_CATEGORY". For debugging, not for display. */
  levelName: string;
  /**
   * What this level is CALLED, stated by the server: "Company", "Main
   * category", "Category", "Sub category". Rendered as-is — a screen that
   * derived this from levelName would tie the words a user reads to a
   * constant's spelling, which is how a rename goes wrong.
   */
  levelLabel: string;
  children: CategoryNode[];
  products: Product[];
}

/** Retained name so existing imports keep working. */
export type Category = CategoryNode;

export interface PriceHistoryEntry {
  id: string;
  dealerPrice: string;
  mrp: string | null;
  effectiveFrom: string;
  sourceNote: string | null;
  recordedAt: string;
  recordedBy: { id: string; name: string; role: string };
}

/** Mirrors the CapturePayload the shared capture component produces. */
export interface CapturePayloadDto {
  gps?: { lat: number; long: number; accuracyMetres: number; takenAt?: string };
  photoDataUrl?: string;
  signatureDataUrl?: string;
  notes?: string;
  clientCapturedAt: string;
}

export type PreOrderType = "REGULAR_SALE" | "FOC" | "PRORATA";

export interface CreatePreOrderInput {
  customerId: string;
  type: PreOrderType;
  lineItems: { productId: string; quantityRequested: number }[];
  notes?: string;
  capture: CapturePayloadDto;
}

export interface PreOrderSummary {
  id: string;
  reference: string;
  type: string;
  status: string;
  notes: string | null;
  createdAt: string;
  customer: { id: string; name: string; phone: string | null };
  createdBy: { id: string; name: string };
  lineItems: {
    productId: string;
    productName: string;
    sku: string;
    quantityRequested: number;
    quantityFulfilled: number;
    quantityOutstanding: number;
    unitPriceAtCapture: string;
  }[];
  totalOutstanding: number;
  orders: { id: string; reference: string; status: string }[];
}

export interface BillForCollection {
  id: string;
  reference: string;
  totalAmount: string;
  collected: string;
  outstanding: string;
  customer: { id: string; name: string; phone: string };
  orderReference: string;
  orderType: string;
}

export type PaymentMethod = "CASH" | "CHEQUE" | "UPI" | "ONLINE";

export interface RecordPaymentInput {
  billId?: string;
  reference?: string;
  amount: number;
  /**
   * MIRRORS PAYMENT_METHODS in src/lib/constants.ts, which the server
   * validates with z.enum and the database pins with a CHECK constraint.
   *
   * It read "CASH" | "CHECK" | "ONLINE", and both errors in that were
   * invisible to the compiler because the screen's own list agreed with it:
   * "CHECK" is not a value the server accepts (it is "CHEQUE"), so every
   * cheque collection was rejected; and UPI - the most common mode in the
   * receipts book this was modelled on - could not be recorded at all.
   */
  paymentMethod: PaymentMethod;
  /**
   * The cheque number, UPI reference or bank UTR.
   *
   * REQUIRED by the server for every method except CASH: cash goes in the
   * cash book and everything else goes in the bank book, and a bank receipt
   * with no instrument reference cannot be reconciled against the statement.
   *
   * It was missing from this type entirely, and the screen was putting the
   * number it collected into `referenceNote` instead - which the server does
   * not check. So a cheque, UPI or online collection was refused whatever the
   * agent typed, and only cash could ever be recorded.
   */
  instrumentNumber?: string;
  /** A free note. NOT the instrument reference - see above. */
  referenceNote?: string;
  /** Number on the counterfoil handed over, where the book was used. */
  manualReceiptNo?: string;
  capture: CapturePayloadDto;
}

/**
 * A payment against a bill THIS application never raised.
 *
 * The customer instead of a bill, and no cap: there is no balance here to
 * check the amount against. Its own type rather than RecordPaymentInput with
 * everything optional, because a type where the required field depends on
 * which endpoint you happen to call is a type that checks nothing.
 */
export interface RecordExternalPaymentInput {
  customerId: string;
  /** The other application's invoice number. Optional - see the service. */
  externalBillReference?: string;
  amount: number;
  paymentMethod: PaymentMethod;
  instrumentNumber?: string;
  manualReceiptNo?: string;
  referenceNote?: string;
  capture: CapturePayloadDto;
}

/** One row of the payments register: either kind. */
export interface RegisterEntry {
  id: string;
  against: "BILL" | "EXTERNAL";
  amount: string;
  paymentMethod: string;
  voucherType: string;
  manualReceiptNo: string | null;
  instrumentNumber: string | null;
  reference: string | null;
  customerName: string;
  collectedAt: string;
  collectedBy: { id: string; name: string };
  referenceNote: string | null;
}

// --- sales -----------------------------------------------------------------

export interface PreOrderRef {
  id: string;
  reference: string;
  type: string;
  status: string;
  customer: {
    id: string;
    name: string;
    isActive: boolean;
    state: string | null;
    gstNumber: string | null;
  };
}

export interface OutstandingLine {
  productId: string;
  sku: string;
  name: string;
  quantityRequested: number;
  quantityFulfilled: number;
  quantityOutstanding: number;
  currentStockQty: number;
  gstRate: number;
}

export interface OrderSummary {
  id: string;
  reference: string;
  type: string;
  status: string;
  createdAt: string;
  customer: { id: string; name: string };
  preOrder: { id: string; reference: string } | null;
  /**
   * Set when the order came from a warranty replacement. "No day order" alone
   * reads as missing data rather than as a service job.
   */
  serviceJob: { id: string; reference: string; outcome: string | null } | null;
  bill: { id: string; reference: string; totalAmount: string } | null;
  lineItems: { quantityFulfilled: number; lineTotal: string }[];
}

/** Money arrives as a decimal string, never a float - see pricing.service. */
export interface OrderLine {
  id: string;
  quantityFulfilled: number;
  unitPrice: string;
  discountAmount: string;
  gstRate: string;
  taxableValue: string;
  cgstAmount: string;
  sgstAmount: string;
  igstAmount: string;
  lineTotal: string;
  /**
   * The discount the bill WILL apply to this line, stated by the server, or
   * null when the billing rule imposes none and a typed discount is the
   * line's own.
   *
   * Non-null for a zero-rated order (the whole line is given away) and for a
   * pro-rata order (the part the customer is not paying for, apportioned from
   * the flat amount the Service Manager agreed). Rendered, never recomputed:
   * the preview showing a different figure from the bill is a defect this
   * screen has already had.
   */
  imposedDiscount: string | null;
  product: {
    id: string;
    sku: string;
    name: string;
    hsnCode: string | null;
    /** On the shelf NOW. Billing is what will change it. */
    currentStockQty: number;
  };
}

export interface OrderDetail {
  id: string;
  reference: string;
  type: string;
  status: string;
  notes: string | null;
  createdAt: string;
  prorataAmount: string | null;
  serviceChargeAmount: string | null;
  customer: {
    id: string;
    name: string;
    phone: string | null;
    gstType: string;
    gstNumber: string | null;
    state: string | null;
  };
  preOrder: { id: string; reference: string } | null;
  /**
   * Who the Service Manager named at the outcome step, for the billing
   * screen to pre-select. Null on a sale order and on any service order
   * where nobody was named.
   */
  intendedDeliveryAgent: { id: string; name: string; status: string } | null;
  serviceJob: {
    id: string;
    reference: string;
    outcome: string | null;
    batteryDetails: string;
    /**
     * Who collected the battery. Context beside the picker, NOT the default
     * any more - the Service Manager's choice is the default now.
     */
    collectionAgent: { id: string; name: string; status: string } | null;
  } | null;
  bill: {
    id: string;
    reference: string;
    totalAmount: string;
    billDate: string;
  } | null;
  lineItems: OrderLine[];
  /**
   * Stated by the server, not worked out here. The billing screen has to show
   * CGST + SGST vs IGST before the bill exists, and the place-of-supply rule
   * belongs in one place - it decides a figure that goes on a tax document.
   */
  placeOfSupply: string;
  gstTreatment: "INTRA_STATE" | "INTER_STATE";
  /** How the total is arrived at. ZERO means nothing is charged at all. */
  billTotalRule: "LINE_ITEMS" | "ZERO" | "EXPLICIT";
  /** Convenience for the above; the preview must show zero when true. */
  billsZero: boolean;
  /**
   * A pro-rata order: it bills the flat amount the Service Manager agreed with
   * the customer, and the Billing Executive may not change it. Distinct from
   * billsZero, which charges nothing at all.
   */
  billsAgreedAmount: boolean;
  /**
   * Whether generating the bill takes goods off the shelf. True for a
   * warranty replacement: free to the customer, but a real battery leaves.
   */
  movesStockAtBilling: boolean;
}

export interface BillSummary {
  id: string;
  reference: string;
  billDate: string;
  customerName: string;
  gstTreatment: string;
  taxableValue: string;
  totalAmount: string;
  order: { id: string; reference: string; type: string } | null;
  /** Paid so far, summed from the payments themselves. */
  amountCollected: string;
  /** What is left. Derived, never a column written when the bill was raised. */
  outstandingAmount: string;
  isSettled: boolean;
}

export interface BillDetail {
  id: string;
  reference: string;
  billDate: string;
  customerName: string;
  customerGstNumber: string | null;
  placeOfSupply: string;
  gstTreatment: "INTRA_STATE" | "INTER_STATE";
  subTotal: string;
  discountTotal: string;
  taxableValue: string;
  cgstAmount: string;
  sgstAmount: string;
  igstAmount: string;
  totalAmount: string;
  paymentMethod: string | null;
  generatedAt: string;
  generatedBy: { id: string; name: string } | null;
  collectedAmount: string;
  outstandingAmount: string;
  order: {
    id: string;
    reference: string;
    type: string;
    status: string;
    preOrder: { id: string; reference: string } | null;
    lineItems: OrderLine[];
  } | null;
  payments: {
    id: string;
    amount: string;
    paymentMethod: string;
    voucherType: string | null;
    collectedAt: string;
  }[];
}

// --- admin -----------------------------------------------------------------

/**
 * A user as the Admin module sees one. Deliberately no password hash: the
 * server never selects it, so it cannot arrive here to be leaked into a log
 * or a React devtools snapshot.
 */
export interface ManagedUser {
  id: string;
  name: string;
  /** The primary role, derived by the server from `roles`. Display only. */
  role: Role;
  /** Every role this person holds, alphabetical. */
  roles: Role[];
  status: string;
  /** Null for the password-only accounts. */
  mobileNumber: string | null;
  /** Null for the OTP accounts. */
  username: string | null;
  mustChangePassword: boolean;
  passwordUpdatedAt: string | null;
  failedLoginAttempts: number;
  /** Set while a password account is locked out after repeated failures. */
  lockedUntil: string | null;
  createdAt: string;
  createdBy: { id: string; name: string } | null;
}

export interface AuditEvent {
  id: string;
  action: string;
  entityType: string;
  entityId: string | null;
  /** JSON text, not an object: SQL Server has no Json column type. */
  payload: string | null;
  createdAt: string;
  actor: { id: string; name: string; role: string } | null;
}

// --- inventory -------------------------------------------------------------

export interface StockLevel {
  id: string;
  sku: string;
  name: string;
  currentStockQty: number;
  /** Null when no threshold has been set, which is not the same as zero. */
  reorderLevel: number | null;
  isActive: boolean;
  category: { id: string; name: string } | null;
  outOfStock: boolean;
  /** Derived, never stored, so changing a reorder level takes effect at once. */
  belowReorder: boolean;
}

export interface StockSummary {
  total: number;
  outOfStock: number;
  belowReorder: number;
  unitsOnHand: number;
  withoutReorderLevel: number;
}

export interface StockMovementResult {
  id: string;
  quantityDelta: number;
  runningBalanceAfter: number;
  createdAt: string;
}

export interface StockMovement {
  id: string;
  quantityDelta: number;
  reason: "STOCK_IN" | "BILL_DEDUCTION" | "CORRECTION";
  referenceId: string | null;
  runningBalanceAfter: number;
  notes: string | null;
  createdAt: string;
  product: { id: string; sku: string; name: string };
  createdBy: { id: string; name: string } | null;
}

export interface Reconciliation {
  checked: number;
  movements: number;
  balanced: boolean;
  drifted: {
    id: string;
    sku: string;
    name: string;
    cached: number;
    fromLedger: number;
    drift: number;
  }[];
}

// --- service ---------------------------------------------------------------

/**
 * The four the business named. SERVICEABLE and REPLACEMENT_FOC were the old
 * spellings of OK and FOC; migration 12 rewrote the rows, so nothing the API
 * returns carries them any more.
 */
export type ServiceOutcome = "OK" | "FOC" | "PRORATA" | "REJECTED";
export type WarrantyStatus = "IN_WARRANTY" | "OUT_OF_WARRANTY" | "UNKNOWN";

export interface ServiceJob {
  id: string;
  reference: string;
  status: string;
  /** Off the battery, scanned or typed. Null for jobs collected before it
   *  was recorded. */
  serialNumber: string | null;
  /**
   * LEGACY free text. No longer asked for at collection - the model says what
   * the battery is - so it is an empty string on anything collected since,
   * and still shown wherever it is non-empty, because on older jobs it is the
   * only description there is.
   */
  batteryDetails: string;
  /**
   * Where the battery sat in the catalogue tree WHEN IT WAS COLLECTED,
   * deepest-first with each node holding its parent - pass it to hierarchyOf
   * to read it in order.
   *
   * Derived by the server from the model, and a snapshot: it does not follow
   * the product if the Store Manager re-files it later.
   *
   * Null only on jobs collected before the column existed.
   */
  category: ProductCategoryChain | null;
  /**
   * The model the battery was collected as. Null only on jobs collected
   * before it was recorded.
   */
  collectedProduct: {
    id: string;
    sku: string;
    name: string;
    isActive: boolean;
  } | null;
  itemCount: number;
  /**
   * LEGACY. Not asked for any more - the Service Manager checks warranty
   * manually and the business said it does not belong in the app. "UNKNOWN"
   * on everything diagnosed since; a real value on older jobs, where it
   * records a judgement somebody made.
   */
  warrantyStatus: WarrantyStatus;
  outcome: ServiceOutcome | null;
  diagnosisNotes: string | null;
  isRepairable: boolean | null;
  collectedAt: string;
  receivedAt: string | null;
  diagnosedAt: string | null;
  customer: {
    id: string;
    name: string;
    phone: string | null;
    gstNumber: string | null;
    state: string | null;
    isActive: boolean;
  };
  collectionAgent: { id: string; name: string } | null;
  serviceManager: { id: string; name: string } | null;
  technician: { id: string; name: string } | null;
  order: {
    id: string;
    reference: string;
    type: string;
    status: string;
    bill: { id: string; reference: string; totalAmount: string } | null;
  } | null;
}

export interface ServiceJobDetail extends ServiceJob {
  outcomeLabel: string | null;
  proofs: {
    id: string;
    kind: string;
    capturedAt: string;
    notes: string | null;
    capturedBy: { id: string; name: string } | null;
  }[];
}

// --- delivery ---------------------------------------------------------------

export type DeliveryScope = "unassigned" | "mine" | "open" | "all";

/** Where the agent is going, and how to find it when the address is vague. */
export interface DeliveryCustomer {
  id: string;
  name: string;
  phone: string | null;
  address: string | null;
  state: string | null;
  geoNotes: string | null;
  lastGeoLat: string | null;
  lastGeoLong: string | null;
}

export interface DeliveryTask {
  id: string;
  status: string;
  /**
   * What the task was raised for. Not what is owed NOW - see
   * outstandingAmount, which the server derives from the bill's payments.
   * Null for a service return: nothing was sold.
   */
  codAmountDue: string | null;
  /** Live, derived. This is the figure to ask the customer for. */
  outstandingAmount: string;
  collectsMoney: boolean;
  carrying: "ORDER" | "SERVICE_JOB";
  /** The reference a human would quote - the order's or the job's. */
  subjectReference: string;
  failureReason: string | null;
  deliveredAt: string | null;
  createdAt: string;
  assignedTo: { id: string; name: string; role: Role } | null;
  order: {
    id: string;
    reference: string;
    type: string;
    status: string;
    customer: DeliveryCustomer;
    serviceJob: { id: string; reference: string; status: string } | null;
    lineItems: {
      id: string;
      quantityFulfilled: number;
      product: { id: string; sku: string; name: string };
    }[];
    bill: {
      id: string;
      reference: string;
      totalAmount: string;
      payments: { id: string; amount: string }[];
    } | null;
  } | null;
  serviceJob: {
    id: string;
    reference: string;
    status: string;
    outcome: string | null;
    /** What is being delivered. Null only on jobs that predate the model. */
    collectedProduct: { id: string; sku: string; name: string } | null;
    /** Legacy description, non-empty only on older jobs. */
    batteryDetails: string;
    itemCount: number;
    customer: DeliveryCustomer;
  } | null;
}

export interface DeliveryTaskDetail extends DeliveryTask {
  proofs: {
    id: string;
    kind: string;
    capturedAt: string;
    notes: string | null;
    capturedBy: { id: string; name: string } | null;
  }[];
}

export interface DeliveryQueueGaps {
  /** Billed orders that never reached Delivery. */
  orders: { id: string; reference: string; customer: string; outstanding: string }[];
  /** Batteries finished in Service that never reached Delivery. */
  serviceJobs: {
    id: string;
    reference: string;
    customer: string;
    status: string;
    defaultAgentId: string | null;
  }[];
  /**
   * Replacements whose order is not billed yet. NOT a gap - they are waiting
   * on the Billing Executive, and the delivery task appears when the bill
   * does. Reported so "it is in Service but not in Delivery" has an answer.
   */
  awaitingBilling: { id: string; reference: string; orderReference: string }[];
}

export interface DeliveryAgent {
  id: string;
  name: string;
  role: Role;
}

/**
 * One row of "Your work now".
 *
 * `module` drives the accent colour and the nav badge; `href` is the
 * already-filtered list, so a tap lands on the rows the number counted
 * rather than on a module landing the person has to filter again.
 */
export interface WorkItem {
  key: string;
  module: Exclude<Module, "ADMIN">;
  count: number;
  title: string;
  subtitle: string;
  href: string;
  /**
   * A queue ("waiting") or a state that should not exist ("alert"). The
   * server decides, because it is a judgement about what the number means.
   */
  tone: "waiting" | "alert";
  /** Money still to collect across this work, in rupees, where that applies. */
  toCollect?: string;
}

export interface MyWork {
  items: WorkItem[];
  /**
   * Billed orders and finished batteries that never reached the Delivery
   * queue. Zero for anyone without the Delivery module.
   */
  missingDeliveries: number;
  /** Customers with no phone number. Zero for anyone but a manager. */
  missingPhones: number;
}
